import React, {useContext, useEffect, useState, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {Context} from '../index';
import MyButton from '../UI/MyButton/MyButton';
import {MaterialReactTable} from "material-react-table";
import ReceiptService from '../services/ReceiptService';
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    Container,
    Paper,
    Fab
} from '@mui/material';
import {Add, ExitToApp} from '@mui/icons-material';

export default function Receipts() {
    const {store} = useContext(Context);
    const [receipts, setReceipts] = useState([]);
    const navigate = useNavigate();

    const pagination = {
        pageSize: 100
    };

    const columns = useMemo(
        () => [
            {
                accessorFn: (row) => {
                    const counterparties = row.counterparties || [];
                    if (counterparties.length === 0) {
                        return 'Нет контрагентов';
                    }
                    if (counterparties.length === 1) {
                        return counterparties[0].name;
                    }
                    return `${counterparties.length} контрагента`;
                },
                id: 'counterparties',
                header: 'Контрагент',
                size: 200,
                Cell: ({ row }) => {
                    const counterparties = row.original.counterparties || [];
                    if (counterparties.length === 0) {
                        return <span style={{ color: '#999' }}>Нет контрагентов</span>;
                    }
                    if (counterparties.length === 1) {
                        return (
                            <div>
                                <div>{counterparties[0].name}</div>
                                <div style={{ fontSize: '10px', color: '#666' }}>
                                    {counterparties[0].itemsCount} товаров
                                </div>
                            </div>
                        );
                    }

                    // Для нескольких контрагентов показываем список с деталями
                    return (
                        <div>
                            <div style={{ fontWeight: 'bold' }}>
                                {counterparties.length} контрагента:
                            </div>
                            {counterparties.map((cp, index) => (
                                <div key={cp.id} style={{
                                    fontSize: '10px',
                                    color: '#666',
                                    marginTop: '2px'
                                }}>
                                    • {cp.name} ({cp.itemsCount} тов.)
                                </div>
                            ))}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'store.name',
                header: 'Склад',
                size: 200
            },
            {
                accessorFn: (row) => {
                    const date = new Date(row.receiptDate);
                    return date.toLocaleDateString('ru-RU');
                },
                id: 'receiptDate',
                header: 'Дата приемки',
                size: 150
            },
            {
                accessorFn: (row) => row.items?.length || 0,
                id: 'itemsCount',
                header: 'Товаров',
                size: 100
            },
            {
                accessorKey: 'status',
                header: 'Статус',
                size: 120,
                Cell: ({ row }) => {
                    const status = row.original.status;
                    const hasSendError = row.original.hasSendError;
                    const errorMessage = row.original.errorMessage;

                    const statusMap = {
                        draft: 'Черновик',
                        sent: 'Отправлен',
                        error: 'Ошибка'
                    };

                    // Особый статус для черновиков с ошибками отправки
                    let displayStatus = statusMap[status] || status;
                    let color = status === 'sent' ? '#4caf50' : '#ff9800';

                    if (status === 'draft' && hasSendError) {
                        displayStatus = 'Черновик (ошибка)';
                        color = '#f44336';
                    }

                    return (
                        <div>
                            <span style={{
                                color: color,
                                fontWeight: 'bold'
                            }}>
                                {displayStatus}
                            </span>
                            {hasSendError && errorMessage && (
                                <div style={{
                                    fontSize: '10px',
                                    color: '#666',
                                    marginTop: '2px',
                                    maxWidth: '150px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }} title={errorMessage}>
                                    {errorMessage}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                accessorFn: (row) => {
                    const date = new Date(row.updatedAt);
                    return date.toLocaleDateString('ru-RU') + ' ' + date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
                },
                id: 'updatedAt',
                header: 'Обновлен',
                size: 150
            }
        ],
        [],
    );

    useEffect(() => {
        getUserReceipts();
    }, []);

    async function getUserReceipts() {
        try {
            const response = await ReceiptService.getUserReceipts();
            setReceipts(response.data);
        } catch (error) {
            alert('Ошибка загрузки приемок');
        }
    }

    function handleCreateReceipt() {
        navigate('/receipts/new');
    }

    function handleEditReceipt(receiptId) {
        navigate(`/receipts/${receiptId}`);
    }

    async function handleDeleteReceipt(receiptId) {
        if (!window.confirm('Вы уверены, что хотите удалить эту приемку?')) {
            return;
        }

        try {
            await ReceiptService.deleteReceipt(receiptId);
            alert('Приемка удалена');
            await getUserReceipts();
        } catch (error) {
            alert('Ошибка удаления приемки');
        }
    }

    function handleLogout() {
        navigate('/login');
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Хедер */}
            <AppBar position="static" sx={{
                background: 'linear-gradient(135deg, #f24405 0%, #a62f04 100%)',
                marginBottom: 3
            }}>
                <Toolbar>
                    <div style={{ flexGrow: 1, fontSize: '1.25rem', fontWeight: 500 }}>
                        МойСклад - Приемки
                    </div>
                    <div style={{ marginRight: '1rem' }}>
                        {store.user.email}
                    </div>
                    <Button
                        color="inherit"
                        startIcon={<ExitToApp />}
                        onClick={async () => {
                            await store.logout();
                            handleLogout();
                        }}
                    >
                        Выйти
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Основной контент */}
            <Container maxWidth="xl">
                {/* Заголовок и кнопка создания */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <h2 style={{ margin: 0, fontSize: '2.125rem', fontWeight: 400 }}>
                        Мои приемки
                    </h2>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleCreateReceipt}
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' },
                            fontSize: '16px',
                            padding: '10px 20px'
                        }}
                    >
                        Новая приемка
                    </Button>
                </Box>

                {/* Таблица приемок */}
                <Paper elevation={2} sx={{ padding: 2 }}>
                    {receipts.length > 0 ? (
                        <MaterialReactTable
                            columns={columns}
                            data={receipts}
                            enablePagination={false}
                            enableTopToolbar={false}
                            enableBottomToolbar={false}
                            enableSorting={false}
                            enableColumnFilters={false}
                            enableColumnActions={false}
                            enableDensityToggle={false}
                            enableFullScreenToggle={false}
                            enableHiding={false}
                            enableRowActions
                            muiTableContainerProps={{
                                sx: {
                                    maxHeight: '600px'
                                }
                            }}
                            renderRowActions={({ row }) => (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditReceipt(row.original._id);
                                        }}
                                        sx={{
                                            backgroundColor: row.original.status === 'draft' ? '#f24405' : '#ff9800',
                                            '&:hover': {
                                                backgroundColor: row.original.status === 'draft' ? '#a62f04' : '#f57c00'
                                            },
                                            fontSize: '12px',
                                            minWidth: 'auto'
                                        }}
                                    >
                                        {row.original.status === 'draft' ? 'Редактировать' : 'Просмотр'}
                                    </Button>
                                    {(row.original.status === 'draft' || row.original.status === 'error') && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteReceipt(row.original._id);
                                            }}
                                            sx={{
                                                fontSize: '12px',
                                                minWidth: 'auto'
                                            }}
                                        >
                                            Удалить
                                        </Button>
                                    )}
                                </Box>
                            )}
                            muiTableBodyRowProps={({row}) => ({
                                sx: {
                                    backgroundColor: row.original.hasSendError ? '#ffebee' : '#ffffff',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: row.original.hasSendError ? '#ffcdd2' : '#f5f5f5',
                                    }
                                },
                                onClick: () => handleEditReceipt(row.original._id)
                            })}
                        />
                    ) : (
                        <Box sx={{
                            textAlign: 'center',
                            padding: 6,
                            color: '#666',
                            backgroundColor: '#fafafa',
                            borderRadius: 2
                        }}>
                            <h3 style={{ margin: 0, marginBottom: '1rem', fontSize: '1.25rem', fontWeight: 500 }}>
                                У вас пока нет приемок
                            </h3>
                            <div style={{ fontSize: '1rem' }}>
                                Нажмите "Новая приемка" чтобы создать первую
                            </div>
                        </Box>
                    )}
                </Paper>
            </Container>

            {/* Floating Action Button для мобильных устройств */}
            <Fab
                aria-label="add"
                onClick={handleCreateReceipt}
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    backgroundColor: '#f24405',
                    '&:hover': { backgroundColor: '#a62f04' },
                    display: { xs: 'flex', sm: 'none' } // Показываем только на мобильных
                }}
            >
                <Add />
            </Fab>
        </Box>
    );
}